import { Box, Link, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { isValidGuid, sanitizeInput } from '../../utils/formUtils';

import WizardNavigation from './WizardNavigation';

interface Props {
  onNext: (data: { tenantId: string }) => void;
  onBack: () => void;
  activeStep: number;
  credentials: {
    tenantId: string;
  };
}

const Step2TenantId: React.FC<Props> = ({ onNext, onBack, activeStep, credentials }) => {
  const [tenantId, setTenantId] = useState(credentials.tenantId || '');
  const [error, setError] = useState('');

  const handleTenantIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setTenantId(sanitizedValue);
    if (error) {
      validateTenantId(sanitizedValue);
    }
  };

  const validateTenantId = (value: string) => {
    if (!value) {
      setError('Tenant ID is required.');
      return false;
    } else if (!isValidGuid(value)) {
      setError('Please enter a valid Tenant ID (GUID format).');
      return false;
    } else {
      setError('');
      return true;
    }
  };

  const handleBlur = () => {
    validateTenantId(tenantId);
  };

  const handleNext = () => {
    if (validateTenantId(tenantId)) {
      onNext({ tenantId });
    }
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>
        Step 2: Find your Tenant ID
      </Typography>
      <Typography variant="body1" paragraph>
        This ID is the unique identifier for your entire organization’s Microsoft account.
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary={
              <span>
                1. Navigate to the{' '}
                <Link href="https://portal.azure.com" target="_blank" rel="noopener">
                  Azure Portal
                </Link>{' '}
                and sign in.
              </span>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText primary='2. In the top search bar, search for and select "Microsoft Entra ID".' />
        </ListItem>
        <ListItem>
          <ListItemText primary="3. On the “Overview” page, you will see the Tenant ID. Copy this value." />
        </ListItem>
      </List>
      <TextField
        label="Tenant ID"
        value={tenantId}
        onChange={handleTenantIdChange}
        onBlur={handleBlur}
        fullWidth
        required
        variant="outlined"
        margin="normal"
        sx={{ mt: 2 }}
        error={!!error}
        helperText={error || 'Example: 12345678-abcd-1234-abcd-1234567890ab'}
      />
      <WizardNavigation
        activeStep={activeStep}
        onBack={onBack}
        onNext={handleNext}
        isNextDisabled={!tenantId || !!error}
      />
    </Box>
  );
};

export default Step2TenantId;
